import React, { useState, useCallback } from 'react';
import { IconChevronLeft, IconChevronRight } from './Icons';
import './AppShell.css';

// localStorage keys for the desktop pane collapse state, so a user's choice to
// hide the Today stream / Work rail survives reloads.
const LEFT_KEY = 'symphonate-pane-left-collapsed';
const RIGHT_KEY = 'symphonate-pane-right-collapsed';

function readCollapsed(key) {
  try {
    return localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeCollapsed(key, value) {
  try {
    if (value) localStorage.setItem(key, '1');
    else localStorage.removeItem(key);
  } catch {
    /* ignore persistence failures */
  }
}

// Direction B "Stream" large-screen shell. Composes the existing Lobby (Today
// stream) and ChatThread into panes rather than swapping between them — see
// docs/direction-b-multidevice-plan.md and the handoff's StreamLarge.jsx.
//   tablet  : two-pane  — Today (left) + detail (right)
//   desktop : three-pane — Today (left) + thread (center) + Work rail (right)
// The phone layout never renders this; App.js keeps its original swap there.
//
// The side panels (the Today stream and, on desktop, the Work rail) are
// expandable/collapsible: a rail edge toggle folds each away to a thin strip so
// the center thread can take the full width, and the choice is persisted.
export default function AppShell({ breakpoint, lobby, detail, workRail }) {
  const isDesktop = breakpoint === 'desktop';
  const [leftCollapsed, setLeftCollapsed] = useState(() => readCollapsed(LEFT_KEY));
  const [rightCollapsed, setRightCollapsed] = useState(() => readCollapsed(RIGHT_KEY));

  const toggleLeft = useCallback(() => {
    setLeftCollapsed((prev) => {
      const next = !prev;
      writeCollapsed(LEFT_KEY, next);
      return next;
    });
  }, []);
  const toggleRight = useCallback(() => {
    setRightCollapsed((prev) => {
      const next = !prev;
      writeCollapsed(RIGHT_KEY, next);
      return next;
    });
  }, []);

  return (
    <div
      className={`app-shell app-shell-${breakpoint}${leftCollapsed ? ' left-collapsed' : ''}${
        isDesktop && rightCollapsed ? ' right-collapsed' : ''
      }`}
    >
      <div className={`app-pane app-pane-today${leftCollapsed ? ' pane-collapsed' : ''}`}>
        <button
          type="button"
          className="pane-toggle pane-toggle-left"
          onClick={toggleLeft}
          aria-expanded={!leftCollapsed}
          aria-label={leftCollapsed ? 'Expand the Today panel' : 'Collapse the Today panel'}
          title={leftCollapsed ? 'Expand panel' : 'Collapse panel'}
        >
          {leftCollapsed ? <IconChevronRight size={16} /> : <IconChevronLeft size={16} />}
        </button>
        {leftCollapsed ? (
          <button
            type="button"
            className="pane-collapsed-strip"
            onClick={toggleLeft}
            title="Expand panel"
          >
            <span className="pane-collapsed-label">Today</span>
          </button>
        ) : (
          lobby
        )}
      </div>

      <div className="app-pane app-pane-detail">{detail}</div>

      {isDesktop && (
        <div className={`app-pane app-pane-rail${rightCollapsed ? ' pane-collapsed' : ''}`}>
          <button
            type="button"
            className="pane-toggle pane-toggle-right"
            onClick={toggleRight}
            aria-expanded={!rightCollapsed}
            aria-label={rightCollapsed ? 'Expand the Work panel' : 'Collapse the Work panel'}
            title={rightCollapsed ? 'Expand panel' : 'Collapse panel'}
          >
            {rightCollapsed ? <IconChevronLeft size={16} /> : <IconChevronRight size={16} />}
          </button>
          {rightCollapsed ? (
            <button
              type="button"
              className="pane-collapsed-strip"
              onClick={toggleRight}
              title="Expand panel"
            >
              <span className="pane-collapsed-label">Work</span>
            </button>
          ) : (
            workRail
          )}
        </div>
      )}
    </div>
  );
}

// Empty state for the detail pane when no thread is open.
export function DetailEmpty() {
  return (
    <div className="app-pane-empty">
      <div className="app-pane-empty-inner">
        <p className="app-pane-empty-title">No thread open</p>
        <p className="app-pane-empty-sub">Pick a thread from the stream to start reading.</p>
      </div>
    </div>
  );
}
